import axios from "axios";
import { adminApiUrl, publicApiUrl, signedInApiUrl, MATCH_RESULTS } from "../util/Constants";
import { setPlayerElo } from "../util/HelperUtils";
import type { Player, PlayerDetail, LadderStatsData, UnlinkedUser } from "../types/domain";

export const playersApi = {
  getById: async (id: number | string): Promise<PlayerDetail> => {
    const playerId = Number(id);
    const { data } = await axios.get<PlayerDetail>(`${publicApiUrl}/players/${id}`);
    data.matches.forEach((match) => {
      match.isWin =
        (match.state === MATCH_RESULTS.PLAYER_ONE_WON && match.playerOne.id === playerId) ||
        (match.state === MATCH_RESULTS.PLAYER_TWO_WON && match.playerTwo.id === playerId);
    });
    return data;
  },

  listByLadder: async (ladderId: number): Promise<Player[]> => {
    const { data } = await axios.get<Player[]>(`${publicApiUrl}/players?ladderId=${ladderId}`);
    data.sort((a, b) => (b.elo ?? 0) - (a.elo ?? 0));
    data.forEach((player, index) => {
      player.filter = (player.firstname + (player.nickname ?? "") + player.lastname).toUpperCase();
      player.rank = index + 1;
    });
    return data;
  },

  // Full player roster (for the PlayerPicker when setting up a tournament). Any logged-in user
  // can call this — the backend exposes it under /auth/players.
  listAllForOrganizer: async (ladderId: number): Promise<Player[]> => {
    const { data } = await axios.get<Player[]>(`${signedInApiUrl}/players`);
    data.sort((a, b) => a.lastname.localeCompare(b.lastname));
    data.forEach((player) => {
      player.filter = (player.firstname + (player.nickname ?? "") + player.lastname).toUpperCase();
      setPlayerElo(player, ladderId);
    });
    return data;
  },

  getLadderStats: async (ladderId: number, year?: number | null): Promise<LadderStatsData> => {
    const yearParam = year ? `?year=${year}` : "";
    const { data } = await axios.get<LadderStatsData>(`${publicApiUrl}/stats/ladder/${ladderId}${yearParam}`);
    return data;
  },

  // Editing players (incl. re-linking user accounts) is admin-only.
  update: async (player: Pick<Player, "id"> & Partial<Player>): Promise<Player> => {
    const { data } = await axios.put<Player>(`${adminApiUrl}/players/${player.id}`, player);
    return data;
  },

  create: async (player: Partial<Player>): Promise<Player> => {
    const { data } = await axios.post<Player>(`${signedInApiUrl}/players`, player);
    return data;
  },

  fetchUnlinked: async (): Promise<UnlinkedUser[]> => {
    const { data } = await axios.get<UnlinkedUser[]>(`${adminApiUrl}/players/unlinked`);
    data.sort((a, b) => (a.username ?? "").localeCompare(b.username ?? ""));
    return [{ id: null }, ...data];
  },
};
